import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Pricing from "@/pages/Pricing";
import Admin from "@/pages/Admin";
import AdminPayments from "@/pages/AdminPayments";
import ProfileSettings from "@/pages/ProfileSettings";
import TeamSettings from "@/pages/TeamSettings";
import BillingSettings from "@/pages/BillingSettings";
import OutreachSettings from "@/pages/OutreachSettings";
import MobileSettings from "@/pages/MobileSettings";
import SettingsApi from "@/pages/SettingsApi";
import Developers from "@/pages/Developers";
import DevReference from "@/pages/DevReference";
import DevGlossary from "@/pages/DevGlossary";
import DevRecipe from "@/pages/DevRecipe";
import FindMobile from "@/pages/FindMobile";
import FindEmail from "@/pages/FindEmail";
import LegalPage from "@/pages/LegalPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Landing from "@/pages/Landing";
import VerifyEmail from "@/pages/VerifyEmail";
import CheckInbox from "@/pages/CheckInbox";
import CaseStudies from "@/pages/CaseStudies";
import AdminAffiliates from "@/pages/AdminAffiliates";
import PartnerDashboard from "@/pages/PartnerDashboard";

function ProtectedShell({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen grid place-items-center text-ink-400 text-sm">Loading…</div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Block unverified users from any protected page except /check-inbox itself.
  if (
    user.email_verified === false &&
    typeof window !== "undefined" &&
    window.location.pathname !== "/check-inbox"
  ) {
    return <Navigate to="/check-inbox" replace />;
  }
  return (
    <div className="min-h-screen bg-ink-50">
      <Header />
      {children}
    </div>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen grid place-items-center text-ink-400 text-sm">Loading…</div>
    );
  }
  if (user) {
    // Unverified users must hit /check-inbox first — otherwise they land on
    // the dashboard, get blocked by EMAIL_NOT_VERIFIED, and bounce confusingly.
    if (user.email_verified === false) return <Navigate to="/check-inbox" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <Login />
          </PublicOnly>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnly>
            <Register />
          </PublicOnly>
        }
      />
      <Route path="/" element={<Landing />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedShell>
            <Dashboard />
          </ProtectedShell>
        }
      />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/case-studies" element={<CaseStudies />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/check-inbox" element={<ProtectedShell><CheckInbox /></ProtectedShell>} />
      <Route
        path="/admin"
        element={
          <ProtectedShell>
            <Admin />
          </ProtectedShell>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <ProtectedShell>
            <AdminPayments />
          </ProtectedShell>
        }
      />
      <Route
        path="/admin/affiliates"
        element={
          <ProtectedShell>
            <AdminAffiliates />
          </ProtectedShell>
        }
      />
      <Route
        path="/partner"
        element={
          <ProtectedShell>
            <PartnerDashboard />
          </ProtectedShell>
        }
      />
      <Route
        path="/settings/profile"
        element={
          <ProtectedShell>
            <ProfileSettings />
          </ProtectedShell>
        }
      />
      <Route
        path="/settings/team"
        element={
          <ProtectedShell>
            <TeamSettings />
          </ProtectedShell>
        }
      />
      <Route
        path="/settings/billing"
        element={
          <ProtectedShell>
            <BillingSettings />
          </ProtectedShell>
        }
      />
      <Route
        path="/settings/outreach"
        element={
          <ProtectedShell>
            <OutreachSettings />
          </ProtectedShell>
        }
      />
      <Route
        path="/settings/mobile"
        element={
          <ProtectedShell>
            <MobileSettings />
          </ProtectedShell>
        }
      />
      <Route
        path="/settings/api"
        element={
          <ProtectedShell>
            <SettingsApi />
          </ProtectedShell>
        }
      />
      <Route
        path="/developers"
        element={<Developers />}
      />
      <Route
        path="/developers/reference"
        element={<DevReference />}
      />
      <Route
        path="/developers/glossary"
        element={<DevGlossary />}
      />
      <Route
        path="/developers/recipes/:slug"
        element={<DevRecipe />}
      />
      <Route
        path="/find-mobile"
        element={
          <ProtectedShell>
            <FindMobile />
          </ProtectedShell>
        }
      />
      <Route
        path="/find-email"
        element={
          <ProtectedShell>
            <FindEmail />
          </ProtectedShell>
        }
      />
      <Route path="/legal/:slug" element={<LegalPage />} />
      <Route
        path="/forgot-password"
        element={
          <PublicOnly>
            <ForgotPassword />
          </PublicOnly>
        }
      />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster
          position="bottom-right"
          richColors
          toastOptions={{
            classNames: {
              toast: "rounded-sm border border-ink-100 bg-white text-ink-900 font-medium shadow-md",
              title: "font-medium",
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
